/**
 * SeoHealthTile — snapshot of public SEO surfaces (sitemap.xml, rss.xml,
 * robots.txt) so the admin can verify everything is healthy at a glance.
 *
 * Cost-safe: single GET to /admin/seo/health. No LLM calls.
 */
import { useEffect, useState } from "react";
import {
    Rss,
    Map as MapIcon,
    Bot,
    ExternalLink,
    Link as LinkIcon,
    Loader2,
    RefreshCw,
    ShieldCheck,
    AlertTriangle,
    AlertCircle,
    Info,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Row({ icon: Icon, label, count, url, hint, testid }) {
    return (
        <div
            className="flex items-start gap-3 py-2.5 border-b border-bl/30 last:border-0"
            data-testid={testid}
        >
            <Icon className="h-4 w-4 mt-0.5 text-bl-muted flex-shrink-0" />
            <div className="flex-1">
                <div className="text-xs font-medium text-bl">
                    {label}
                    {count !== undefined && (
                        <span className="ml-2 text-bl-muted font-mono-bl">
                            {count}
                        </span>
                    )}
                </div>
                {hint && (
                    <div className="text-[11px] text-bl-muted mt-0.5 leading-relaxed">
                        {hint}
                    </div>
                )}
            </div>
            {url && (
                <a
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    className="bl-btn-ghost text-[11px] gap-1 flex-shrink-0"
                >
                    Open <ExternalLink className="h-3 w-3" />
                </a>
            )}
        </div>
    );
}

export default function SeoHealthTile() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [auditOpen, setAuditOpen] = useState(false);
    const [auditFull, setAuditFull] = useState(null);
    const [auditLoading, setAuditLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const r = await axios.get(`${API}/admin/seo/health`, {
                withCredentials: true,
            });
            setData(r.data);
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Could not load SEO health.",
            );
        } finally {
            setLoading(false);
        }
    };

    const openAudit = async () => {
        setAuditOpen(true);
        if (auditFull) return; // cached
        setAuditLoading(true);
        try {
            const r = await axios.get(`${API}/admin/seo/schema-audit`, {
                withCredentials: true,
            });
            setAuditFull(r.data);
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Could not load audit.",
            );
        } finally {
            setAuditLoading(false);
        }
    };

    const autoFix = async () => {
        if (
            !window.confirm(
                "Back-fill missing schema.Article.image / datePublished / dateModified / publisher fields from existing DB data on every flagged article? This is deterministic and reversible (warnings will be regenerated if the data changes).",
            )
        )
            return;
        setAuditLoading(true);
        try {
            const r = await axios.post(
                `${API}/admin/seo/schema-audit/auto-fix`,
                {},
                { withCredentials: true },
            );
            const fr = r.data.fix_report;
            toast.success(
                `Auto-fixed ${fr.articles_modified} article${fr.articles_modified === 1 ? "" : "s"} · ${fr.total_fixed_fields} field${fr.total_fixed_fields === 1 ? "" : "s"} set.`,
            );
            setAuditFull(r.data.audit);
            // Refresh the SEO health tile summary
            await load();
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Auto-fix failed.",
            );
        } finally {
            setAuditLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    if (loading && !data) {
        return (
            <div
                className="bl-card p-5 flex items-center gap-3 text-sm text-bl-muted"
                data-testid="seo-health-loading"
            >
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading SEO health…
            </div>
        );
    }
    if (!data) return null;

    const latest = data.latest_published || {};

    return (
        <div className="bl-card p-5 space-y-3" data-testid="seo-health-tile">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <h3 className="font-display font-semibold text-base text-bl tracking-tight">
                    SEO surfaces · live
                </h3>
                <button
                    type="button"
                    onClick={load}
                    disabled={loading}
                    className="bl-btn-ghost text-xs gap-1"
                    data-testid="seo-health-refresh"
                >
                    <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
                    Refresh
                </button>
            </div>

            <p className="text-[11px] text-bl-muted leading-relaxed">
                All three feeds regenerate live from the DB on every request — no
                manual rebuild needed when you publish, rename, or seed a redirect.
            </p>

            <div className="rounded border border-bl/40">
                <Row
                    icon={MapIcon}
                    label="sitemap.xml"
                    count={`${data.sitemap?.url_count ?? 0} URLs`}
                    url={data.sitemap?.url}
                    hint={`${data.sitemap?.top_level_count} top-level · ${data.sitemap?.tool_count} tools · ${data.sitemap?.article_count} articles. Hero images now annotated with <image:image> for Google Image Search.`}
                    testid="seo-health-sitemap"
                />
                <Row
                    icon={Rss}
                    label="rss.xml"
                    count={`${data.rss?.item_count ?? 0} items`}
                    url={data.rss?.url}
                    hint="Last 30 published articles. Subscribers (feed readers, Apple News, aggregators) see new content within minutes."
                    testid="seo-health-rss"
                />
                <Row
                    icon={LinkIcon}
                    label="Active 301 redirects"
                    count={`${data.redirects?.count ?? 0}`}
                    hint="Legacy slugs that automatically forward to canonical URLs. Manage above ↑."
                    testid="seo-health-redirects"
                />
                <SchemaAuditRow
                    audit={data.schema_audit}
                    onOpen={openAudit}
                />
                <Row
                    icon={Bot}
                    label="robots.txt"
                    url={data.robots_url}
                    hint="Disallows /admin, /dashboard, /checkout. References sitemap.xml."
                    testid="seo-health-robots"
                />
            </div>

            {latest?.slug && (
                <div className="text-[11px] text-bl-muted flex flex-wrap gap-x-4 gap-y-1">
                    <span>
                        Latest published:{" "}
                        <a
                            href={`/learn/${latest.slug}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-bl underline"
                        >
                            {latest.title}
                        </a>
                        {" · "}
                        {latest.published_at
                            ? new Date(latest.published_at).toLocaleDateString()
                            : ""}
                    </span>
                    {data.latest_snapshot && (
                        <NightlyAuditPill snap={data.latest_snapshot} />
                    )}
                </div>
            )}

            <Dialog open={auditOpen} onOpenChange={setAuditOpen}>
                <DialogContent
                    className="max-w-3xl max-h-[85vh] overflow-y-auto"
                    data-testid="schema-audit-modal"
                >
                    <DialogHeader>
                        <DialogTitle className="font-display tracking-tight text-bl flex items-center gap-2">
                            <ShieldCheck className="h-4 w-4 text-bl-primary" />
                            Structured-data audit
                        </DialogTitle>
                        <DialogDescription className="text-xs text-bl-muted leading-relaxed">
                            Per-article validation of JSON-LD schemas (Article,
                            FAQPage, BreadcrumbList) + DB hygiene checks
                            (meta_description, hero_image, category). Catches
                            subtle SEO regressions before Google's crawler does.
                        </DialogDescription>
                    </DialogHeader>
                    {auditLoading || !auditFull ? (
                        <div className="py-10 text-center text-sm text-bl-muted">
                            <Loader2 className="h-5 w-5 mx-auto animate-spin mb-2" />
                            Running audit…
                        </div>
                    ) : (
                        <div className="space-y-3 text-sm">
                            <div className="flex flex-wrap items-center justify-between gap-3 text-xs">
                                <div className="flex flex-wrap items-center gap-3">
                                    <span className="text-bl-muted">
                                        {auditFull.articles_clean} of{" "}
                                        {auditFull.articles_checked} clean
                                    </span>
                                    <Badge
                                        level="error"
                                        n={auditFull.totals?.error || 0}
                                    />
                                    <Badge
                                        level="warn"
                                        n={auditFull.totals?.warn || 0}
                                    />
                                    <Badge
                                        level="info"
                                        n={auditFull.totals?.info || 0}
                                    />
                                </div>
                                {(auditFull.totals?.warn || 0) +
                                    (auditFull.totals?.error || 0) >
                                    0 && (
                                    <button
                                        type="button"
                                        onClick={autoFix}
                                        disabled={auditLoading}
                                        className="bl-btn-primary text-xs gap-1"
                                        data-testid="audit-auto-fix"
                                    >
                                        <ShieldCheck className="h-3 w-3" />
                                        Auto-fix all
                                    </button>
                                )}
                            </div>
                            <div className="rounded border border-bl/40 divide-y divide-bl/30">
                                {auditFull.per_article.map((a) => (
                                    <details
                                        key={a.slug}
                                        className="px-3 py-2 group"
                                        data-testid={`audit-row-${a.slug}`}
                                    >
                                        <summary className="cursor-pointer list-none [&::-webkit-details-marker]:hidden flex items-center gap-3 text-xs">
                                            <span className="text-bl-muted text-[10px]">
                                                ▸
                                            </span>
                                            <span
                                                className={
                                                    "font-medium truncate flex-1 " +
                                                    (a.warning_count === 0
                                                        ? "text-bl"
                                                        : "text-bl")
                                                }
                                            >
                                                {a.title || a.slug}
                                            </span>
                                            {a.warning_count === 0 ? (
                                                <span className="inline-flex items-center gap-1 text-[10px] text-[var(--bl-green)]">
                                                    <ShieldCheck className="h-3 w-3" />
                                                    clean
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 text-[10px] text-[var(--bl-rust)]">
                                                    <AlertTriangle className="h-3 w-3" />
                                                    {a.warning_count} issue
                                                    {a.warning_count === 1
                                                        ? ""
                                                        : "s"}
                                                </span>
                                            )}
                                        </summary>
                                        <div className="mt-2 ml-5 space-y-2">
                                            {a.warnings.map((w, idx) => (
                                                <WarningLine
                                                    key={idx}
                                                    warning={w}
                                                />
                                            ))}
                                            <LiveValidatorRow slug={a.slug} />
                                        </div>
                                    </details>
                                ))}
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Badge({ level, n }) {
    const cfg = {
        error: { cls: "bg-[var(--bl-rust-tint)] text-[var(--bl-rust)]", label: "errors" },
        warn: { cls: "bg-[var(--bl-amber-tint)] text-[var(--bl-amber)]", label: "warnings" },
        info: { cls: "bg-bl-bg text-bl-muted", label: "info" },
    }[level];
    return (
        <span
            className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium font-mono-bl ${cfg.cls}`}
            data-testid={`audit-badge-${level}`}
        >
            {n} {cfg.label}
        </span>
    );
}

function WarningLine({ warning }) {
    const Icon =
        warning.level === "error"
            ? AlertCircle
            : warning.level === "warn"
              ? AlertTriangle
              : Info;
    const color =
        warning.level === "error"
            ? "text-[var(--bl-rust)]"
            : warning.level === "warn"
              ? "text-[var(--bl-amber)]"
              : "text-bl-muted";
    return (
        <div className="flex items-start gap-2 text-[11px]">
            <Icon className={`h-3 w-3 mt-0.5 flex-shrink-0 ${color}`} />
            <code className="text-bl font-mono-bl flex-shrink-0">
                {warning.field}
            </code>
            <span className="text-bl-muted">{warning.message}</span>
        </div>
    );
}

function NightlyAuditPill({ snap }) {
    if (!snap?.ran_at) return null;
    const ran = new Date(snap.ran_at);
    const hoursAgo = (Date.now() - ran.getTime()) / 1000 / 3600;
    const label =
        hoursAgo < 1
            ? "just now"
            : hoursAgo < 24
              ? `${Math.round(hoursAgo)}h ago`
              : `${Math.round(hoursAgo / 24)}d ago`;
    const regs = snap.regression_count || 0;
    const cls = regs > 0
        ? "text-[var(--bl-rust)]"
        : "text-[var(--bl-green)]";
    return (
        <span
            className={`inline-flex items-center gap-1 ${cls}`}
            data-testid="seo-health-nightly-pill"
        >
            · Last automated check: {label}
            {" — "}
            {regs > 0
                ? `${regs} regression${regs === 1 ? "" : "s"}`
                : "no regressions"}
        </span>
    );
}

function LiveValidatorRow({ slug }) {
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);

    const fetchPreview = async () => {
        if (preview) return;
        setLoading(true);
        try {
            const r = await axios.get(
                `${API}/admin/seo/schema-audit/preview/${encodeURIComponent(slug)}`,
                { withCredentials: true },
            );
            setPreview(r.data);
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Could not load live preview.",
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="mt-1 border-t border-bl/20 pt-2"
            data-testid={`live-validator-${slug}`}
        >
            {!preview ? (
                <button
                    type="button"
                    onClick={fetchPreview}
                    disabled={loading}
                    className="bl-btn-ghost text-[11px] gap-1"
                    data-testid={`live-validator-load-${slug}`}
                >
                    {loading ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                        <ExternalLink className="h-3 w-3" />
                    )}
                    Validate live URL
                </button>
            ) : (
                <div className="space-y-1.5">
                    <div className="text-[11px] text-bl-muted">
                        {preview.schema_count} JSON-LD block
                        {preview.schema_count === 1 ? "" : "s"} will be injected ·{" "}
                        <span className="text-bl">
                            {Object.keys(preview.schemas).join(" · ")}
                        </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <a
                            href={preview.google_rich_results_url}
                            target="_blank"
                            rel="noreferrer"
                            className="bl-btn-ghost text-[11px] gap-1"
                            data-testid={`live-validator-google-${slug}`}
                        >
                            Test on Google
                            <ExternalLink className="h-3 w-3" />
                        </a>
                        <a
                            href={preview.google_schema_validator_url}
                            target="_blank"
                            rel="noreferrer"
                            className="bl-btn-ghost text-[11px] gap-1"
                            data-testid={`live-validator-schemaorg-${slug}`}
                        >
                            Schema.org validator
                            <ExternalLink className="h-3 w-3" />
                        </a>
                        <a
                            href={preview.live_url}
                            target="_blank"
                            rel="noreferrer"
                            className="bl-btn-ghost text-[11px] gap-1"
                            data-testid={`live-validator-live-${slug}`}
                        >
                            Open live article
                            <ExternalLink className="h-3 w-3" />
                        </a>
                    </div>
                    <p className="text-[10px] text-bl-muted italic leading-relaxed">
                        Google's Rich Results Test runs JavaScript so it sees
                        the same JSON-LD a real Googlebot crawl would see. Use
                        it as the final ground-truth check before publishing.
                    </p>
                </div>
            )}
        </div>
    );
}


function SchemaAuditRow({ audit, onOpen }) {
    const totals = audit?.totals || {};
    const errors = totals.error || 0;
    const warns = totals.warn || 0;
    const infos = totals.info || 0;
    const checked = audit?.articles_checked || 0;
    const clean = audit?.articles_clean || 0;
    const allClean = errors === 0 && warns === 0;
    const Icon = allClean ? ShieldCheck : AlertTriangle;
    const iconColor = allClean
        ? "text-[var(--bl-green)]"
        : errors > 0
          ? "text-[var(--bl-rust)]"
          : "text-[var(--bl-amber)]";

    return (
        <div
            className="flex items-start gap-3 py-2.5 border-b border-bl/30 last:border-0"
            data-testid="seo-health-schema-audit"
        >
            <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${iconColor}`} />
            <div className="flex-1">
                <div className="text-xs font-medium text-bl flex items-center gap-2 flex-wrap">
                    Schema audit
                    {allClean ? (
                        <span className="text-[10px] text-[var(--bl-green)]">
                            all clean
                        </span>
                    ) : (
                        <>
                            {errors > 0 && (
                                <span
                                    className="text-[10px] font-mono-bl text-[var(--bl-rust)]"
                                    data-testid="audit-summary-errors"
                                >
                                    {errors} errors
                                </span>
                            )}
                            {warns > 0 && (
                                <span
                                    className="text-[10px] font-mono-bl text-[var(--bl-amber)]"
                                    data-testid="audit-summary-warns"
                                >
                                    {warns} warnings
                                </span>
                            )}
                            {infos > 0 && (
                                <span className="text-[10px] font-mono-bl text-bl-muted">
                                    {infos} info
                                </span>
                            )}
                        </>
                    )}
                </div>
                <div className="text-[11px] text-bl-muted mt-0.5">
                    {clean} of {checked} articles clean · validates Article /
                    FAQPage / BreadcrumbList JSON-LD against Google rich-result
                    requirements.
                </div>
            </div>
            <button
                type="button"
                onClick={onOpen}
                className="bl-btn-ghost text-[11px] flex-shrink-0"
                data-testid="audit-open-details"
            >
                Details
            </button>
        </div>
    );
}
